import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { resolveMediaUrl } from "@/components/FileUpload";
import { PlayableVideo } from "@/components/PlayableVideo";
import { videoPosterFor } from "@/lib/neutralPoster";
import { gradientCss } from "@/lib/gradients";
import { storyFontStyle } from "@/lib/storyFonts";
import { Eye, Archive, ShieldCheck, RefreshCw, Loader2, CalendarClock, Trash2, Send, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

const bgStyle = (bg) => (bg && bg.startsWith("/verse-bg/")
  ? { backgroundImage: `url(${bg})`, backgroundSize: "cover", backgroundPosition: "center" }
  : { background: gradientCss(bg) || "var(--gradient-navy)" });

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return ""; } };
const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

// "À venir" — the author's stories queued to publish later, with manage actions.
// ISO UTC → valeur <input type="datetime-local"> (heure locale).
const toLocalInput = (iso) => {
  try { const d = new Date(iso); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); } catch { return ""; }
};

// Modification en place d'une story programmée : date/heure (+ texte pour une story texte), jamais supprimée.
const ScheduledEditForm = ({ story, onSaved, onCancel, t }) => {
  const [text, setText] = useState(story.text || "");
  const [when, setWhen] = useState(toLocalInput(story.scheduled_at));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    let iso;
    try { iso = new Date(when).toISOString(); } catch { iso = null; }
    if (!iso || new Date(iso).getTime() <= Date.now() + 60000) { toast.error(t("schedule.past", "Choisis une date et une heure futures.")); return; }
    setSaving(true);
    try {
      const body = { scheduled_at: iso };
      if (story.media_type === "text" && text.trim() && text.trim() !== (story.text || "").trim()) body.text = text.trim();
      const { data } = await api.patch(`/stories/${story.id}`, body);
      toast.success(t("story.schedule_updated", "Story programmée mise à jour ✓"));
      onSaved(data?.story || { ...story, ...body });
    } catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || t("common.error", "Erreur")); } finally { setSaving(false); }
  };
  return (
    <div className="rounded-xl p-3 border space-y-2" style={{ borderColor: "var(--yalola-border)" }} data-testid={`scheduled-story-edit-${story.id}`}>
      {story.media_type === "text" && (
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} className="w-full rounded-xl border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }} data-testid={`scheduled-story-edit-text-${story.id}`} />
      )}
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: "var(--yalola-text-muted)" }}>
          <CalendarClock size={14} />
          <input type="datetime-local" value={when} min={toLocalInput(new Date(Date.now() + 5 * 60000).toISOString())} onChange={(e) => setWhen(e.target.value)} className="rounded-lg border px-2 py-1 text-sm outline-none" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)", color: "var(--yalola-text)" }} data-testid={`scheduled-story-edit-when-${story.id}`} />
        </label>
        <span className="flex-1" />
        <button type="button" onClick={onCancel} disabled={saving} className="h-8 px-3 rounded-full text-xs font-semibold inline-flex items-center gap-1" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }} data-testid={`scheduled-story-edit-cancel-${story.id}`}><X size={13} /> {t("common.cancel", "Annuler")}</button>
        <button type="button" onClick={save} disabled={saving} className="h-8 px-3 rounded-full text-xs font-semibold text-white inline-flex items-center gap-1 disabled:opacity-60" style={{ background: "var(--yalola-primary)" }} data-testid={`scheduled-story-edit-save-${story.id}`}>{saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} {t("common.save", "Enregistrer")}</button>
      </div>
    </div>
  );
};

const ScheduledList = ({ items, loading, onPublish, onCancel, onSaved, busyId, t }) => {
  const [editing, setEditing] = useState(null);
  if (loading) return <div className="py-10 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }}>…</div>;
  if (!items.length) return (
    <div className="py-10 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="story-scheduled-empty">
      {t("story.scheduled_empty", "Aucune story programmée. Planifiez-en une depuis « Créer une story ».")}
    </div>
  );
  return (
    <div className="space-y-2" data-testid="story-scheduled-list">
      {items.map((s) => editing === s.id ? (
        <ScheduledEditForm key={s.id} story={s} t={t} onCancel={() => setEditing(null)} onSaved={(ns) => { onSaved(ns); setEditing(null); }} />
      ) : (
        <div key={s.id} className="flex items-center gap-3 rounded-xl p-2 border" style={{ borderColor: "var(--yalola-border)" }} data-testid={`scheduled-story-${s.id}`}>
          <div className="h-16 w-10 rounded-lg overflow-hidden bg-black shrink-0 relative">
            {s.media_type === "text" ? (
              <div className="absolute inset-0 grid place-items-center p-1" style={bgStyle(s.bg)}>
                <span className="text-white text-[7px] font-bold text-center line-clamp-3" style={storyFontStyle(s.font)}>{s.text}</span>
              </div>
            ) : s.media_type === "video" ? (
              <video src={`${resolveMediaUrl(s.media_url)}#t=0.4`} poster={videoPosterFor(resolveMediaUrl(s.media_url), s.poster_url ? resolveMediaUrl(s.poster_url) : null)} preload="metadata" muted playsInline className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <img src={resolveMediaUrl(s.media_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">{s.text || (s.media_type === "video" ? t("story.video", "Vidéo") : t("story.photo", "Photo"))}</div>
            <div className="text-xs flex items-center gap-1 mt-0.5" style={{ color: "var(--yalola-primary)" }}>
              <CalendarClock size={12} /> {fmtDateTime(s.scheduled_at)}
            </div>
          </div>
          <button onClick={() => setEditing(s.id)} disabled={busyId === s.id} className="h-8 w-8 grid place-items-center rounded-full shrink-0 disabled:opacity-60" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text)" }} aria-label={t("post.schedule_edit", "Modifier")} data-testid={`edit-scheduled-${s.id}`}>
            <Pencil size={14} />
          </button>
          <button
            onClick={() => onPublish(s)}
            disabled={busyId === s.id}
            className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1.5 shrink-0 disabled:opacity-60"
            style={{ background: "var(--yalola-primary)", color: "#fff" }}
            data-testid={`publish-now-${s.id}`}
          >
            {busyId === s.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} {t("story.publish_now", "Publier")}
          </button>
          <button onClick={() => onCancel(s)} disabled={busyId === s.id} className="h-8 w-8 grid place-items-center rounded-full text-red-500 hover:bg-red-50 shrink-0 disabled:opacity-60" data-testid={`cancel-scheduled-${s.id}`}>
            <Trash2 size={15} />
          </button>
        </div>
      ))}
    </div>
  );
};

const ArchiveGrid = ({ stories, loading, onOpen, t, emptyLabel }) => {
  if (loading) return <div className="py-10 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }}>…</div>;
  if (!stories.length) return (
    <div className="py-10 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="story-archive-empty">
      {emptyLabel || t("story.archive_empty", "Aucune story archivée pour le moment.")}
    </div>
  );
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="story-archive-grid">
      {stories.map((s) => (
        <button key={s.id} onClick={() => onOpen(s)} className="relative aspect-[9/16] rounded-xl overflow-hidden bg-black transition-transform hover:scale-[1.02]" data-testid={`archive-story-${s.id}`}>
          {s.media_type === "text" ? (
            <div className="absolute inset-0 grid place-items-center p-2" style={bgStyle(s.bg)}>
              <span className="text-white text-[10px] font-bold text-center line-clamp-4" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.4)", ...storyFontStyle(s.font) }}>{s.text}</span>
            </div>
          ) : s.media_type === "video" ? (
            <video src={`${resolveMediaUrl(s.media_url)}#t=0.4`} poster={videoPosterFor(resolveMediaUrl(s.media_url), s.poster_url ? resolveMediaUrl(s.poster_url) : null)} preload="metadata" muted playsInline className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <img src={resolveMediaUrl(s.media_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-1 left-1.5 right-1.5 text-white text-[9px] flex items-center justify-between">
            <span className="flex items-center gap-0.5"><Eye size={10} /> {s.views_count ?? 0}</span>
            <span className="opacity-80">{fmtDate(s.created_at)}</span>
          </div>
          {s.expired && <span className="absolute top-1 left-1 text-[8px] font-bold uppercase bg-black/60 text-white rounded px-1">{t("story.archived", "Expirée")}</span>}
          {s.is_church_story && <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-white grid place-items-center"><ShieldCheck size={10} style={{ color: "var(--yalola-primary)" }} /></span>}
        </button>
      ))}
    </div>
  );
};

const SingleViewer = ({ story, onClose, onRepublish, republishing, t }) => (
  <div className="fixed inset-0 z-[60] bg-black/90 grid place-items-center p-4" onClick={onClose} data-testid="archive-single-viewer">
    <div className="max-w-sm w-full aspect-[9/16] rounded-2xl overflow-hidden bg-black relative" onClick={(e) => e.stopPropagation()}>
      {story.media_type === "text" ? (
        <div className="w-full h-full grid place-items-center p-8" style={bgStyle(story.bg)}>
          <p className="text-white text-2xl font-bold text-center whitespace-pre-line break-words" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.4)", ...storyFontStyle(story.font) }}>{story.text}</p>
        </div>
      ) : story.media_type === "video" ? (
        <PlayableVideo src={story.media_url} autoPlay className="w-full h-full" videoClassName="w-full h-full object-contain bg-black" testId="story-archive-video" />
      ) : (
        <img src={resolveMediaUrl(story.media_url)} alt="" className="w-full h-full object-cover" />
      )}
      <div className="absolute top-3 left-3 right-3 flex items-center text-white text-xs">
        <span className="flex items-center gap-1 bg-black/40 rounded-full px-2 py-1"><Eye size={12} /> {story.views_count ?? 0}</span>
        <button onClick={onClose} className="ml-auto bg-black/40 rounded-full px-3 py-1" data-testid="archive-single-close">{t("story.close", "Fermer")}</button>
      </div>
      <div className="absolute bottom-3 left-3 right-3 flex justify-center">
        <button
          onClick={() => onRepublish(story)}
          disabled={republishing}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold shadow-lg disabled:opacity-60"
          style={{ background: "var(--yalola-primary)", color: "#fff" }}
          data-testid={`archive-republish-${story.id}`}
        >
          {republishing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
          {t("story.republish", "Republier")}
        </button>
      </div>
    </div>
  </div>
);

// Private story archive (author-only): grid of past stories + a simple single viewer.
// Usable as a modal (open/onOpenChange) or inline (Profile tab).
export const StoryArchive = ({ open, onOpenChange, inline = false }) => {
  const { t } = useApp();
  const [tab, setTab] = useState("active");
  const [stories, setStories] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSched, setLoadingSched] = useState(true);
  const [active, setActive] = useState(null);
  const [republishing, setRepublishing] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const reload = () =>
    api.get("/stories/archive").then(({ data }) => setStories(data.stories || [])).catch(() => setStories([]));
  const reloadScheduled = () =>
    api.get("/stories/scheduled").then(({ data }) => setScheduled(data.stories || [])).catch(() => setScheduled([]));

  useEffect(() => {
    if (!inline && !open) return;
    setLoading(true); setLoadingSched(true);
    reload().finally(() => setLoading(false));
    reloadScheduled().finally(() => setLoadingSched(false));
  }, [open, inline]);

  const republish = async (story) => {
    setRepublishing(true);
    try {
      await api.post(`/stories/${story.id}/republish`);
      toast.success(t("story.republished", "Story republiée !"));
      window.dispatchEvent(new Event("yalola:story_created"));
      setActive(null);
      reload();
    } catch (e) {
      toast.error(e?.friendlyMessage || "Erreur");
    } finally { setRepublishing(false); }
  };

  const publishNow = async (story) => {
    setBusyId(story.id);
    try {
      await api.post(`/stories/${story.id}/publish-now`);
      toast.success(t("story.published_now", "Story publiée !"));
      window.dispatchEvent(new Event("yalola:story_created"));
      setScheduled((a) => a.filter((x) => x.id !== story.id));
      reload();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBusyId(null); }
  };

  const cancelScheduled = async (story) => {
    setBusyId(story.id);
    try {
      await api.delete(`/stories/${story.id}`);
      toast.success(t("story.scheduled_cancelled", "Story programmée annulée"));
      setScheduled((a) => a.filter((x) => x.id !== story.id));
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBusyId(null); }
  };

  const activeStories = stories.filter((s) => !s.expired);
  const expiredStories = stories.filter((s) => s.expired);

  const tabs = (
    <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="story-archive-tabs">
      {[
        ["active", t("story.tab_active", "En cours"), activeStories.length],
        ["archive", t("story.tab_archive", "Archives"), expiredStories.length],
        ["scheduled", t("story.tab_scheduled", "À venir"), scheduled.length],
      ].map(([key, label, count]) => (
        <button
          key={key}
          onClick={() => setTab(key)}
          className={`pill ${tab === key ? "pill-primary" : "pill-outline"}`}
          data-testid={`story-archive-tab-${key}`}
        >
          {label}{count ? ` (${count})` : ""}
        </button>
      ))}
    </div>
  );

  const content = (
    <>
      {tabs}
      {tab === "scheduled"
        ? <ScheduledList items={scheduled} loading={loadingSched} onPublish={publishNow} onCancel={cancelScheduled} onSaved={(ns) => setScheduled((a) => a.map((x) => (x.id === ns.id ? { ...x, ...ns } : x)).sort((x, y) => (x.scheduled_at || "").localeCompare(y.scheduled_at || "")))} busyId={busyId} t={t} />
        : <ArchiveGrid stories={tab === "active" ? activeStories : expiredStories} loading={loading} onOpen={setActive} t={t} emptyLabel={tab === "active" ? t("story.active_empty", "Aucune story en cours. Elle apparaîtra ici pendant 24 h après publication.") : t("story.archive_empty_expired", "Aucune story archivée pour le moment.")} />}
    </>
  );

  if (inline) {
    return (
      <div data-testid="story-archive-inline">
        {content}
        {active && <SingleViewer story={active} onClose={() => setActive(null)} onRepublish={republish} republishing={republishing} t={t} />}
      </div>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl" data-testid="story-archive-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display"><Archive size={18} /> {t("story.archive_title", "Mes archives de stories")}</DialogTitle>
            <DialogDescription>{t("story.archive_desc", "Vos stories en cours, passées et programmées, visibles uniquement par vous.")}</DialogDescription>
          </DialogHeader>
          <div className="">{content}</div>
        </DialogContent>
      </Dialog>
      {active && <SingleViewer story={active} onClose={() => setActive(null)} onRepublish={republish} republishing={republishing} t={t} />}
    </>
  );
};

export default StoryArchive;
